/** Sentinel dashboard — the program at a glance. */
import { useState, useEffect } from 'react';
import { api, Spinner, Tooltip, Hint, money, STAGE_LABEL, STAGE_TONE, Badge } from './ui';

export default function SentinelDashboard({ onOpenCase, goTo }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let on = true;
    api('/dashboard')
      .then((j) => { if (on) setData(j.dashboard); })
      .catch((e) => on && setErr(e.message))
      .finally(() => on && setLoading(false));
    api('/queue').then((j) => on && setQ(j.queue)).catch(() => {}); // best effort
    return () => { on = false; };
  }, []);

  if (loading) return <Spinner label="Loading your dashboard…" />;
  if (err) return <div className="p-6 text-sm text-red-600">{err}</div>;

  const recoveredPct = data.dollars_identified > 0 ? Math.round((data.dollars_recovered / data.dollars_identified) * 100) : 0;
  const cards = [
    { label: 'Open cases', value: data.open_cases, tone: 'blue',
      tip: 'Investigations that are not yet closed — across every stage from new referral to referred.' },
    { label: '60-day clock warnings', value: data.clock_warnings, tone: data.clock_warnings > 0 ? 'red' : 'green',
      tip: 'Overpayments whose legal 60-day report-and-return deadline is within the next 7 days (or already past). Medicare Advantage plans must report and return identified overpayments within 60 days.' },
    { label: 'Dollars identified', value: money(data.dollars_identified), tone: 'slate',
      tip: 'Total overpayment dollars your investigations have identified.' },
    { label: 'Dollars recovered', value: money(data.dollars_recovered), tone: 'green',
      tip: `Total dollars actually recovered — ${recoveredPct}% of what was identified.` },
    { label: 'Referrals submitted', value: data.referrals_submitted, tone: 'purple',
      tip: 'Cases referred to the government (the I-MEDIC, CMS, OIG, or a state) and marked submitted.' },
  ];

  const stageEntries = Object.entries(data.by_stage || {});

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => (
          <Tooltip key={c.label} text={c.tip}>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 w-full hover:border-slate-300 transition-colors cursor-default">
              <div className={`text-2xl font-bold ${c.tone === 'red' ? 'text-red-600' : c.tone === 'green' ? 'text-emerald-600' : 'text-slate-900'}`}>{c.value}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center">{c.label}</div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Needs attention — pulled from the work queue */}
      {q && (q.needs_triage.length + q.due_soon.length + q.idle.length > 0) && (
        <div className="grid md:grid-cols-3 gap-4">
          <AttnCard title="Needs your decision" items={q.needs_triage} onOpenCase={onOpenCase} goTo={goTo} />
          <AttnCard title="Deadlines coming due" items={q.due_soon} onOpenCase={onOpenCase} goTo={goTo} />
          <AttnCard title="Gone quiet" items={q.idle} onOpenCase={onOpenCase} goTo={goTo} />
        </div>
      )}

      {/* Caseload by stage */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center mb-4">
          <h2 className="font-semibold text-slate-800">Where your cases are right now</h2>
          <Hint text="Every open investigation grouped by what stage it's in. Click a stage to jump to those cases." />
          <button onClick={() => goTo('cases')} className="ml-auto text-sm text-red-600 hover:text-red-700 font-medium">View all cases →</button>
        </div>
        {stageEntries.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No cases yet. Head to <button onClick={() => goTo('intake')} className="text-red-600 font-medium">Intake</button> to log your first referral.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {stageEntries.map(([stage, n]) => (
              <div key={stage} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
                <Badge tone={STAGE_TONE[stage] || 'slate'}>{STAGE_LABEL[stage] || stage}</Badge>
                <span className="text-sm font-semibold text-slate-700">{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center">
        Sentinel checks your FDR exclusion lists and PMRA risk-adjustment data automatically — but only if your plan owns those modules. They're a bonus, never required.
      </p>
    </div>
  );
}

function AttnCard({ title, items, onOpenCase, goTo }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center mb-2">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <span className="ml-auto text-xs font-bold text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">All clear.</p>
      ) : (
        <div className="space-y-1">
          {items.slice(0, 4).map((c) => (
            <button key={c.id} onClick={() => onOpenCase(c.id)} className="block w-full text-left text-xs text-slate-600 hover:text-red-600 truncate">
              <span className="font-mono text-slate-400">{c.case_number}</span> · {c.allegation_summary || '(no summary)'}
            </button>
          ))}
          {items.length > 4 && (
            <button onClick={() => goTo('queue')} className="text-xs text-red-600 font-medium hover:text-red-700">+{items.length - 4} more in Queue →</button>
          )}
        </div>
      )}
    </div>
  );
}
